import getDuration from "@/helpers/get-duration";
import useAuth from "@/utils/hooks/login-auth";
import useResponsive from "@/utils/hooks/Responsive-Screens";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import CircularProgressBar from "../progress/circular-progress";
import { TickCourse } from "../svgcomponents/common-icons";
import {
  LearningPathArticlesIcon,
  LearningPathAssignmentIcon,
  LearningPathLineIcon,
  LearningPathVideoIcon,
  SideBarCancelIcon,
} from "../svgcomponents/lp-common-icons";
import Tooltip from "../tooltip-custom/CustomTooltip";

export const Item = ({
  srno,
  item,
  handleProgress,
  isLearningPathCourse,
  isStandalone,
  incrementAttempts = () => {},
  courseUrl,
  startURL,
}) => {
  const [isInProgress, setIsInProgress] = useState(false);
  const { isAuthenticated, loggedInUser } = useAuth();
  const router = useRouter();
  let learningPathURL = router.query?.learningPathway;
  let courseURL = courseUrl || router.query?.course;
  if (item.courseUrl) {
    courseURL = item.courseUrl;
  }

  let lessonURL = router.query?.lessons;
  let slug = `/${(lessonURL || courseURL)?.replace(/\//g, "")}`;

  let itemUrl = item?.url?.startsWith("/learning-paths/")
    ? `/${item.url.split("/").slice(-1)[0].replace(/\//g, "")}`
    : `/${item?.url?.replace(/\//g, "")}`;
  const isDifferentCourse = item.courseUrl && item.courseUrl !== courseURL;

  let url;

  if (isLearningPathCourse) {
    if (item.cover) {
      url = item.url.startsWith("/learning-paths/")
        ? item.url
        : `/learning-paths/${learningPathURL}/${isDifferentCourse ? item.courseUrl : courseURL}`;
    } else {
      url = `/learning-paths/${learningPathURL}/${isDifferentCourse ? item.courseUrl : courseURL}/${item?.url?.replace(/\//g, "")}`;
    }
  } else {
    if (item.cover) {
      url = `/courses/${item?.url?.replace(/\//g, "")}`;
    } else {
      url = `/courses/${isDifferentCourse ? item.courseUrl : courseURL}/${item.url?.replace(/\//g, "")}`;
    }
  }

  let itemURL = `/academy${startURL ? url + "?start=true" : url}`;

  const isActiveCourseOverview =
    item.cover &&
    !isDifferentCourse &&
    ((isLearningPathCourse &&
      router.asPath.includes(
        `/learning-paths/${learningPathURL}/${courseURL}`
      ) &&
      !lessonURL) ||
      (!isLearningPathCourse &&
        router.asPath.includes(`/courses/${courseURL}`) &&
        !lessonURL));

  const isActiveLesson = !item.cover && slug === itemUrl;

  useEffect(() => {
    if (isActiveLesson) {
      const element = document.querySelector(".lesson.active");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [isActiveLesson]);

  // Effect to handle the "in progress" state
  useEffect(() => {
    if (isActiveLesson && !item?.progress?.isCompleted && loggedInUser) {
      setIsInProgress(true);
    } else if (!isActiveLesson) {
      setIsInProgress(false);
    }
  }, [isActiveLesson, item?.progress?.isCompleted]);

  const handleKeyActivate = (e, handler) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler(e);
    }
  };

  // label for ada accessibility label specific issue - Visible label and accessible name do not match
  let duration = "";
  if (item?.video_lesson) {
    duration =
      item.time_to_read || getDuration(item?.video_lesson[0]?.duration);
  }

  let questionCount = "";
  if (item?.quiz?.assessment && item?.is_quiz) {
    questionCount = `${item.quiz?.question_count} Questions`;
  }

  let status = "";
  if (item?.progress?.isCompleted && loggedInUser) {
    status = "Completed";
  } else if (
    isInProgress ||
    (item?.progress?.hasStarted && !item?.progress?.isCompleted && loggedInUser)
  ) {
    status = "In Progress";
  }

  // Create aria-label that matches what's actually visible
  let labelParts = [
    srno !== 0 ? srno + "." : "",
    item.is_quiz ? "Course Quiz" : item.title,
  ];

  // Only include duration and question count if lesson is NOT completed
  // (because we are not shown visually for completed lessons)
  if (!item?.progress?.isCompleted) {
    if (duration) labelParts.push(duration);
    if (questionCount) labelParts.push(questionCount);
  }

  // Always include status if it exists
  if (status) labelParts.push(status);

  let label = labelParts.filter(Boolean).join(" ");

  return (
    <Tooltip
      position={!item.cover ? "top" : "bottom"}
      disabled={isAuthenticated || !isLearningPathCourse || item.cover}
      text="Log in to access this lesson"
      className="w-100"
    >
      <div
        className={`w-100 lesson ${isActiveLesson || isActiveCourseOverview ? "active" : ""}`}
        role="listitem"
      >
        <a
          href={
            !isAuthenticated && isLearningPathCourse && !item?.cover
              ? null
              : itemURL
          }
          onClick={() => {
            if (!isAuthenticated && !isLearningPathCourse && !item?.cover) {
              incrementAttempts();
            }
          }}
          onKeyDown={(e) => {
            if (!isAuthenticated && !isLearningPathCourse && !item?.cover) {
              handleKeyActivate(e, incrementAttempts);
            }
          }}
          tabIndex={0}
          aria-disabled={
            !isAuthenticated && isLearningPathCourse && !item?.cover
          }
          className={`text-decoration-none underline-focussed`}
          aria-current={
            isActiveLesson || isActiveCourseOverview ? "page" : undefined
          }
          aria-label={label}
        >
          <div className="lesson-title">{`${srno !== 0 ? srno + "." : ""} ${item.is_quiz ? "Course Quiz" : item.title}`}</div>
          {!item.cover ? (
            !item?.progress?.isCompleted ? (
              <div
                className="lesson-duration d-flex gap-2 align-items-center justify-content-start"
                aria-pressed="false"
              >
                {!item?.text_lesson &&
                  (!item?.quiz?.assessment || !item?.is_quiz) && (
                    <LearningPathVideoIcon />
                  )}
                {item?.text_lesson &&
                  (!item?.quiz?.assessment || !item?.is_quiz) && (
                    <LearningPathArticlesIcon />
                  )}
                {item?.quiz?.assessment && item.is_quiz && (
                  <LearningPathAssignmentIcon />
                )}
                {item?.video_lesson
                  ? item.time_to_read ||
                    getDuration(item?.video_lesson[0]?.duration)
                  : ""}
                {item?.quiz?.assessment &&
                  item?.is_quiz &&
                  `${item.quiz?.question_count} Questions `}
                {isInProgress ||
                (item?.progress?.hasStarted &&
                  !item?.progress?.isCompleted &&
                  loggedInUser) ? (
                  <>
                    <LearningPathLineIcon />{" "}
                    <span className="inProgress-text">In Progress</span>
                  </>
                ) : null}
              </div>
            ) : loggedInUser ? (
              <span className="completed-text">Completed</span>
            ) : null
          ) : null}
        </a>
      </div>
    </Tooltip>
  );
};

const LeftSideNav = ({
  courseProgress,
  data,
  close,
  setClose,
  handleProgress,
  learningPathUrl,
  lessons,
  course,
  startCourse,
  isStandalone,
  incrementAttempts,
  isAssessmentAvailable,
  optimisticComplete,
  hadleAssessment,
}) => {
  const router = useRouter();
  const currentCourseURL = router?.query?.course;
  let isLearningPathCourse = router?.query?.learningPathway;
  const { isAuthenticated } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const getCurrentCourseIndex = () => {
    if (!currentCourseURL || !Array.isArray(data)) return 0;

    const index = data.findIndex(
      (courseData) =>
        courseData.url && courseData.url.replace(/\//g, "") === currentCourseURL
    );

    return index >= 0 ? index : 0;
  };

  // Get the current course index
  const currentCourseIndex = getCurrentCourseIndex();

  const combineArrays = (arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return [];
    return arr1.map((obj1) => {
      const matchingObj = arr2.find((obj2) => obj2?.entryUid === obj1?.uid);
      return matchingObj ? { ...obj1, ...matchingObj } : obj1;
    });
  };

  const generateNavData = (courseData) => {
    let arr = courseData?.is_standalone
      ? courseData
      : courseData?.chapters?.[0]?.lessons?.lessons;
    let newList = combineArrays(arr, lessons);

    if (
      courseData.url &&
      courseData.url.replace(/\//g, "") !== currentCourseURL
    ) {
      newList = newList.map((item) => ({
        ...item,
        courseUrl: courseData.url.replace(/\//g, ""),
      }));
    }

    let courseTitle = courseData?.cover_title;

    if (!courseTitle) {
      if (isLearningPathCourse) {
        if (courseData?.url?.replace(/\//g, "") === currentCourseURL) {
          courseTitle =
            data[currentCourseIndex]?.cover_title ||
            (isStandalone ? "Course Content" : "Course Overview");
        } else {
          courseTitle = isStandalone ? "Course Content" : "Course Overview";
        }
      } else {
        courseTitle = isStandalone ? "Course Content" : "Course Overview";
      }
    }

    return [
      {
        title: courseTitle,
        url: isLearningPathCourse
          ? `/learning-paths/${isLearningPathCourse}/${courseData?.url?.replace(/\//g, "")}`
          : courseData.url.replace(/\//g, ""),
        cover: true,
        progress: {
          isCompleted: course?.progress?.isCompleted,
        },
        courseUrl: courseData?.url?.replace(/\//g, ""),
      },
      ...newList,
    ];
  };
  const handleClick = () => {
    if (typeof window !== "undefined") {
      document
        .querySelector(".left-side-nav")
        .classList.add("overflow-x-hidden");
      document.querySelector("iframe").classList.add("text-lesson-iframe");
    }
    setClose(!close);
  };

  return (
    <>
      <nav
        className={`left-sidenav-wrapper sticky-top  ${close ? "collapsed" : "not-collapsed"}`}
        aria-label="Course navigation"
        role="navigation"
        style={{
          height: isMobile || isTablet ? "100%" : "100vh",
          maxHeight: isMobile || isTablet ? "100%" : "100vh",
          paddingBottom: isMobile || isTablet ? "20px" : "0px",
        }}
      >
        <div
          data-swiftype-index="false"
          className={`left-side-nav sticky-top`}
          role="navigation"
          style={{
            overflow: "auto",
            height: "calc(100vh - 60px)",
            maxHeight: "100vh",
            width: "100%",
            display: "block",
          }}
        >
          {isLearningPathCourse ? (
            <div className="text-capitalize">
              <a
                href={`/academy/learning-paths/${isLearningPathCourse}`}
                className=" left-title text-decoration-none padding-8 underline-focussed"
                role="button"
                tabIndex={0}
              >
                ← Back to Learning Path
              </a>
            </div>
          ) : (
            <h3
              className="underline-focussed left-course-title lesson m-0"
              role="button"
            >
              {data?.title}
            </h3>
          )}

          <div className={`close-btn-wrapper`}>
            <button
              onClick={handleClick}
              aria-label={`${close ? "Open" : "Close"} navigation menu`}
              className={` d-inline-block ms-1 radius-4 outline-focussed`}
              role="button"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClick();
                }
              }}
              style={{
                minWidth: "24px",
                minHeight: "24px",
              }}
              tabIndex={0}
            >
              <Tooltip
                text={`${isMobile || isTablet ? "Open" : "Close"} menu`}
                position="left"
              >
                <SideBarCancelIcon />
              </Tooltip>
            </button>
          </div>

          {Array.isArray(data) ? (
            <div role="list">
              {data.map((courseData, index) => {
                const getCourseProgress = courseProgress?.find(
                  (progress) => progress.entryUid === courseData.uid
                );

                return (
                  <div
                    key={index}
                    className="accordion border-0"
                    id="panelsStayOpen"
                    role="region"
                    aria-label={`${courseData?.title} section`}
                  >
                    <div className="accordion-item border-0 ">
                      <h2
                        data-swiftype-index="false"
                        className="accordion-header "
                        id={`heading-${index}`}
                      >
                        <button
                          className={` border-0 underline-focussed custom-accordion-button ${index !== currentCourseIndex ? "collapsed" : ""}`}
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#panelsStayOpen-collapse${index}`}
                          aria-expanded={
                            index === currentCourseIndex ? "true" : "false"
                          }
                          aria-controls={`panelsStayOpen-collapse${index}`}
                          aria-label={`${courseData?.title} section ${index === currentCourseIndex ? "expanded" : "collapsed"}`}
                          tabIndex={0}
                        >
                          <div className="d-flex w-100 align-items-center justify-content-between">
                            <span className="d-flex justify-content-between align-items-center gap-2 pr-40 flex-wrap text-capitalize text-truncate">
                              <span className="text-truncate border-0 p-0 m-0 w-80">
                                {courseData?.title}
                              </span>
                              {getCourseProgress?.progress?.isCompleted ||
                              (optimisticComplete &&
                                index === currentCourseIndex &&
                                isAuthenticated) ? (
                                <TickCourse
                                  width={18}
                                  height={18}
                                />
                              ) : getCourseProgress?.progress?.percentage >
                                0 ? (
                                <CircularProgressBar
                                  progress={
                                    getCourseProgress?.progress?.percentage
                                  }
                                  size={18}
                                />
                              ) : null}
                            </span>

                            <span className="custom-chevron">
                              {index !== currentCourseIndex ? (
                                <svg
                                  width="14"
                                  height="8"
                                  viewBox="0 0 14 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.66854 0.918544C6.8516 0.735485 7.1484 0.735485 7.33146 0.918544L13.5815 7.16854C13.7645 7.3516 13.7645 7.6484 13.5815 7.83146C13.3984 8.01451 13.1016 8.01451 12.9185 7.83146L7 1.91291L1.08146 7.83146C0.898398 8.01451 0.601602 8.01451 0.418544 7.83146C0.235485 7.6484 0.235485 7.3516 0.418544 7.16854L6.66854 0.918544Z"
                                    fill="#475161"
                                  />
                                </svg>
                              ) : index !== currentCourseIndex ? (
                                <svg
                                  width="14"
                                  height="8"
                                  viewBox="0 0 14 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M0.418544 0.168544C0.601602 -0.0145146 0.898398 -0.0145146 1.08146 0.168544L7 6.08709L12.9185 0.168544C13.1016 -0.0145146 13.3984 -0.0145146 13.5815 0.168544C13.7645 0.351602 13.7645 0.648398 13.5815 0.831456L7.33146 7.08146C7.1484 7.26451 6.8516 7.26451 6.66854 7.08146L0.418544 0.831456C0.235485 0.648398 0.235485 0.351602 0.418544 0.168544Z"
                                    fill="#475161"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  width="14"
                                  height="8"
                                  viewBox="0 0 14 8"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M6.66854 0.918544C6.8516 0.735485 7.1484 0.735485 7.33146 0.918544L13.5815 7.16854C13.7645 7.3516 13.7645 7.6484 13.5815 7.83146C13.3984 8.01451 13.1016 8.01451 12.9185 7.83146L7 1.91291L1.08146 7.83146C0.898398 8.01451 0.601602 8.01451 0.418544 7.83146C0.235485 7.6484 0.235485 7.3516 0.418544 7.16854L6.66854 0.918544Z"
                                    fill="#475161"
                                  />
                                </svg>
                              )}
                            </span>
                          </div>
                        </button>
                      </h2>
                      <div
                        id={`panelsStayOpen-collapse${index}`}
                        className={`accordion-collapse collapse ${index === currentCourseIndex ? "show" : ""}`}
                        aria-labelledby={`heading-${index}`}
                        role="region"
                      >
                        <div
                          className="accordion-body"
                          role="list"
                          style={{
                            padding: "0px",
                            maxHeight: "calc(100vh - 200px)",
                            overflow: "auto",
                          }}
                        >
                          {courseData.isStandalone
                            ? generateNavData(courseData)
                                ?.filter((item) => item.cover)
                                ?.map((el, i) => (
                                  <Item
                                    key={i}
                                    srno={i}
                                    item={el}
                                    isStandalone={isStandalone}
                                    learningPathUrl={learningPathUrl}
                                    courseInfo={courseData.course_info}
                                    handleProgress={handleProgress}
                                    isLearningPathCourse={isLearningPathCourse}
                                  />
                                ))
                            : generateNavData(courseData)?.map((el, i) => (
                                <Item
                                  key={i}
                                  srno={i}
                                  item={el}
                                  isStandalone={isStandalone}
                                  learningPathUrl={learningPathUrl}
                                  courseInfo={courseData.course_info}
                                  handleProgress={handleProgress}
                                  isLearningPathCourse={isLearningPathCourse}
                                />
                              ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {isAssessmentAvailable && isAuthenticated && (
                <div
                  className={`lesson lesson-title`}
                  role="listitem"
                >
                  <a
                    onClick={() => hadleAssessment()}
                    className="custom-accordion-button text-grayish-blue"
                    role="button"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        hadleAssessment();
                      }
                    }}
                    tabIndex={0}
                    data-tooltip="Login to track progress"
                    aria-label="Assessment"
                  >
                    Assessment
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div
              role="list"
              aria-label="Course lessons"
            >
              {data.isStandalone
                ? generateNavData(data)
                    ?.filter((item) => item.cover)
                    ?.map((el, index) => (
                      <Item
                        key={index}
                        srno={index}
                        item={el}
                        startURL={startCourse}
                        isLearningPathCourse={isLearningPathCourse}
                        courseInfo={data.course_info}
                        handleProgress={handleProgress}
                        incrementAttempts={incrementAttempts}
                      />
                    ))
                : generateNavData(data)?.map((el, index) => (
                    <Item
                      key={index}
                      srno={index}
                      item={el}
                      startURL={startCourse}
                      isLearningPathCourse={isLearningPathCourse}
                      courseInfo={data.course_info}
                      handleProgress={handleProgress}
                      incrementAttempts={incrementAttempts}
                    />
                  ))}
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default LeftSideNav;
